import { Dispatch, SetStateAction, useCallback } from 'react';
import { useToast } from '@shared/components/Toast';
import { useUpdateViewTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { getEditableViewTemplate } from './EditableViewTemplate';

export function useSaveViewTemplate(
  workflowTemplateId: string,
  setViewTemplate: Dispatch<SetStateAction<API.ViewTemplateFragment>>,
): () => Promise<void> {
  const updateViewTemplate = useUpdateViewTemplate(workflowTemplateId);
  const toast = useToast();

  return useCallback(async () => {
    const save = async (viewTemplate: API.ViewTemplateFragment) => {
      try {
        const { data } = await updateViewTemplate({
          ...viewTemplate,
          defaultReadonlyComponents: true,
        });
        const savedViewTemplate = data?.updateViewTemplate;
        if (savedViewTemplate) {
          // Take what was saved in the shape the server returns it: criteria built from the
          // filter controls leave out fields the server spells out as null, so they never match
          // the saved criteria again and the view would stay modified. Skipped if the view was
          // edited while the save was in flight.
          setViewTemplate(current =>
            current === viewTemplate
              ? { ...current, ...getEditableViewTemplate(savedViewTemplate) }
              : current,
          );
        }
        toast({
          message: i18n('card.actions.save.saveForEveryone.success'),
        });
      } catch (error) {
        toast({
          style: 'warning',
          message: i18n('errors.somethingWentWrong'),
        });
      }
    };

    // accessing current viewTemplate via state-setter, so this callback can be
    // stateless and not change every time viewTemplate changes
    setViewTemplate(viewTemplate => {
      save(viewTemplate);
      return viewTemplate;
    });
  }, [updateViewTemplate, setViewTemplate, toast]);
}
